// Componentes básicos: resistencias, potenciómetros, botones, interruptores, LEDs y zumbador.
import { definePart, centeredRow, PITCH } from './registry.js';

export const LED_COLORS = {
  red: { name: 'Rojo', hex: '#ff2414', vf: 1.8 },
  orange: { name: 'Naranja', hex: '#ff7410', vf: 2.0 },
  yellow: { name: 'Amarillo', hex: '#ffd014', vf: 2.1 },
  green: { name: 'Verde', hex: '#1fe048', vf: 2.2 },
  blue: { name: 'Azul', hex: '#2a62ff', vf: 3.0 },
  white: { name: 'Blanco', hex: '#f2f5ff', vf: 3.0 },
  purple: { name: 'Morado', hex: '#a640ff', vf: 3.1 },
};

const colorOptions = Object.entries(LED_COLORS).map(([value, c]) => ({ value, label: c.name, color: c.hex }));

export const BUTTON_COLORS = {
  red: { name: 'Rojo', hex: '#d42a22' },
  green: { name: 'Verde', hex: '#1f9e3c' },
  blue: { name: 'Azul', hex: '#2456c9' },
  yellow: { name: 'Amarillo', hex: '#e8c11d' },
  black: { name: 'Negro', hex: '#222222' },
  white: { name: 'Blanco', hex: '#e8e8e8' },
};

export const RESISTOR_PRESETS = [10, 22, 47, 100, 150, 220, 330, 470, 680, 1000, 2200, 3300, 4700, 10000, 22000, 47000, 100000, 220000, 470000, 1000000];

definePart({
  type: 'resistor',
  name: 'Resistencia',
  category: 'basicos',
  description: 'Limita la corriente. Las bandas de color cambian según el valor.',
  insertable: true,
  props: [{ key: 'value', label: 'Resistencia', type: 'resistance', default: 220, presets: RESISTOR_PRESETS, runtime: true }],
  pins: [
    { name: '1', label: 'Terminal 1', kind: 'signal', x: -2 * PITCH, y: 0, z: 0, insert: true },
    { name: '2', label: 'Terminal 2', kind: 'signal', x: 2 * PITCH, y: 0, z: 0, insert: true },
  ],
  footprint: { w: 12, d: 3.5, h: 6 },
});

definePart({
  type: 'potentiometer',
  name: 'Potenciómetro',
  category: 'basicos',
  description: 'Resistencia variable con perilla. Conecta los extremos a 5 V y GND y el centro (SIG) a una entrada analógica.',
  insertable: true,
  props: [
    { key: 'resistance', label: 'Resistencia total', type: 'resistance', default: 10000, presets: [1000, 5000, 10000, 50000, 100000], runtime: true },
    { key: 'value', label: 'Posición', type: 'slider', min: 0, max: 1, step: 0.001, default: 0.5, runtime: true, live: true },
  ],
  pins: centeredRow(['GND', 'SIG', 'VCC'], { labels: { GND: 'GND (extremo)', SIG: 'SIG (cursor)', VCC: 'VCC (extremo)' } }),
  footprint: { w: 11, d: 13, h: 20, z: -5.5 },
  interactive: 'knob',
});

definePart({
  type: 'slide-potentiometer',
  name: 'Potenciómetro deslizante',
  category: 'basicos',
  description: 'Potenciómetro lineal de 45 mm de recorrido.',
  props: [
    { key: 'resistance', label: 'Resistencia total', type: 'resistance', default: 10000, presets: [1000, 10000, 100000], runtime: true },
    { key: 'value', label: 'Posición', type: 'slider', min: 0, max: 1, step: 0.001, default: 0.5, runtime: true, live: true },
  ],
  pins: [
    { name: 'GND', label: 'GND', kind: 'gnd', x: -32, y: 4.5, z: -PITCH, dir: [-1, 0, 0] },
    { name: 'SIG', label: 'SIG (cursor)', kind: 'signal', x: -32, y: 4.5, z: 0, dir: [-1, 0, 0] },
    { name: 'VCC', label: 'VCC', kind: 'power', x: -32, y: 4.5, z: PITCH, dir: [-1, 0, 0] },
  ],
  footprint: { w: 66, d: 11, h: 16 },
  interactive: 'slider',
});

definePart({
  type: 'pushbutton',
  name: 'Botón pulsador',
  category: 'entradas',
  description: 'Botón de 6 mm. Los terminales 1a–1b y 2a–2b están unidos por dentro; al presionar se une 1 con 2.',
  insertable: true,
  props: [
    { key: 'color', label: 'Color', type: 'select', default: 'red',
      options: Object.entries(BUTTON_COLORS).map(([value, c]) => ({ value, label: c.name, color: c.hex })) },
    { key: 'key', label: 'Tecla del teclado', type: 'text', default: '', placeholder: 'p. ej. espacio o a' },
  ],
  pins: [
    { name: '1a', label: 'Terminal 1', kind: 'signal', x: -PITCH, y: 0, z: -1.5 * PITCH, insert: true },
    { name: '2a', label: 'Terminal 2', kind: 'signal', x: PITCH, y: 0, z: -1.5 * PITCH, insert: true },
    { name: '1b', label: 'Terminal 1', kind: 'signal', x: -PITCH, y: 0, z: 1.5 * PITCH, insert: true },
    { name: '2b', label: 'Terminal 2', kind: 'signal', x: PITCH, y: 0, z: 1.5 * PITCH, insert: true },
  ],
  buses: [['1a', '1b'], ['2a', '2b']],
  footprint: { w: 8, d: 9, h: 9 },
  interactive: 'button',
});

definePart({
  type: 'slide-switch',
  name: 'Interruptor deslizante',
  category: 'entradas',
  description: 'Interruptor de un polo y dos posiciones: el terminal central se une al 1 o al 3.',
  insertable: true,
  props: [{ key: 'position', label: 'Posición', type: 'select', default: 'left', runtime: true,
    options: [{ value: 'left', label: 'Izquierda (2–1)' }, { value: 'right', label: 'Derecha (2–3)' }] }],
  pins: centeredRow(['1', '2', '3'], { labels: { 1: 'Terminal 1', 2: 'Común', 3: 'Terminal 3' } }),
  footprint: { w: 9, d: 5, h: 8 },
  interactive: 'toggle',
});

definePart({
  type: 'led',
  name: 'LED',
  category: 'luces',
  description: 'LED de 5 mm. La pata larga es el ánodo (+). Necesita una resistencia en serie (220 Ω es buen valor).',
  insertable: true,
  props: [{ key: 'color', label: 'Color', type: 'select', default: 'red', options: colorOptions }],
  pins: [
    { name: 'C', label: 'Cátodo (−)', kind: 'gnd', x: -PITCH / 2, y: 0, z: 0, insert: true },
    { name: 'A', label: 'Ánodo (+)', kind: 'power', x: PITCH / 2, y: 0, z: 0, insert: true },
  ],
  footprint: { w: 6.5, d: 6.5, h: 14 },
});

definePart({
  type: 'rgb-led',
  name: 'LED RGB',
  category: 'luces',
  description: 'Tres LEDs (rojo, verde y azul) en una cápsula con terminal común (cátodo o ánodo).',
  insertable: true,
  props: [{ key: 'common', label: 'Terminal común', type: 'select', default: 'cathode', structural: true,
    options: [{ value: 'cathode', label: 'Cátodo común (−)' }, { value: 'anode', label: 'Ánodo común (+)' }] }],
  pins: (props) => centeredRow(['R', 'COM', 'G', 'B'], {
    labels: { R: 'Rojo', COM: props.common === 'anode' ? 'Común (+)' : 'Común (−)', G: 'Verde', B: 'Azul' },
    kinds: { COM: props.common === 'anode' ? 'power' : 'gnd' },
  }),
  footprint: { w: 10, d: 6.5, h: 14 },
});

definePart({
  type: 'buzzer',
  name: 'Zumbador',
  category: 'actuadores',
  description: 'Piezo de 12 mm. El pasivo suena con tone(); el activo suena solo con 5 V.',
  insertable: true,
  props: [{ key: 'mode', label: 'Tipo', type: 'select', default: 'passive',
    options: [{ value: 'passive', label: 'Pasivo (usa tone)' }, { value: 'active', label: 'Activo (2.3 kHz)' }] }],
  pins: [
    { name: 'NEG', label: '− (GND)', kind: 'gnd', x: -1.5 * PITCH, y: 0, z: 0, insert: true },
    { name: 'POS', label: '+ (señal)', kind: 'power', x: 1.5 * PITCH, y: 0, z: 0, insert: true },
  ],
  footprint: { w: 12.5, d: 12.5, h: 10 },
});

// Capacitores --------------------------------------------------------------------------------------

/** '100 nF', '4.7 µF', '22 pF' a partir de faradios. */
export function formatFarads(f) {
  const trim = (n) => String(Math.round(n * 100) / 100);
  if (f >= 1e-6) return trim(f / 1e-6) + ' µF';
  if (f >= 1e-9) return trim(f / 1e-9) + ' nF';
  return trim(f / 1e-12) + ' pF';
}

/** Código de 3 cifras de un cerámico: dos cifras y el número de ceros, en pF (104 = 10·10⁴ pF = 100 nF). */
export function ceramicCode(f) {
  const pf = Math.round(f / 1e-12);
  const exp = Math.max(0, Math.floor(Math.log10(pf)) - 1);
  return String(Math.round(pf / 10 ** exp)) + exp;
}

// Valores de un surtido de cerámicos (en pF). La clave es lo que se guarda en el proyecto.
export const CERAMIC_VALUES = { '10p': 10, '22p': 22, '47p': 47, '100p': 100, '220p': 220, '470p': 470, '1n': 1e3, '2.2n': 2.2e3,
  '4.7n': 4.7e3, '10n': 1e4, '22n': 2.2e4, '47n': 4.7e4, '100n': 1e5, '220n': 2.2e5, '470n': 4.7e5, '1u': 1e6 };

export function ceramicFarads(props) {
  return (CERAMIC_VALUES[props.value] ?? 1e5) * 1e-12;
}

definePart({
  type: 'capacitor-ceramic',
  name: 'Capacitor cerámico',
  category: 'basicos',
  description: 'Guarda carga sin polaridad (10 pF a 1 µF). El número impreso es su valor en pF: 104 = 10 × 10⁴ pF = 100 nF.',
  keywords: 'capacitor condensador ceramico lenteja disco 104 103 100nf 22pf filtro desacoplo',
  insertable: true,
  props: [{ key: 'value', label: 'Capacitancia', type: 'select', default: '100n',
    options: Object.entries(CERAMIC_VALUES).map(([value, pf]) => ({ value, label: `${formatFarads(pf * 1e-12)} (${ceramicCode(pf * 1e-12)})` })) }],
  variants: [['100n', '100 nF (104)'], ['10n', '10 nF (103)'], ['22p', '22 pF (220)']].map(([value, v]) => ({ label: `Capacitor cerámico ${v}`, props: { value } })),
  pins: [
    { name: '1', label: 'Terminal 1', kind: 'signal', x: -PITCH / 2, y: 0, z: 0, insert: true },
    { name: '2', label: 'Terminal 2', kind: 'signal', x: PITCH / 2, y: 0, z: 0, insert: true },
  ],
  footprint: { w: 7.5, d: 3.5, h: 12 },
  help: 'Con una resistencia R en serie se carga en τ = R·C (al 63 % en τ y casi al 100 % en 5τ). ' +
    'Tolera 50 V. No se simulan la tolerancia (±10–20 %) ni la pérdida de capacitancia con el voltaje de los cerámicos de alta capacitancia.',
});

// Electrolíticos radiales de uso general (85 °C). Valores en µF y voltajes nominales de un surtido.
export const ELECTROLYTIC_VALUES = ['0.47', '1', '2.2', '3.3', '4.7', '10', '22', '33', '47', '100', '220', '330', '470', '1000', '2200'];
export const ELECTROLYTIC_VOLTS = ['6.3', '10', '16', '25', '50'];
// Tamaño típico de la cápsula (Ø × alto en mm) hasta cierta capacitancia, por voltaje.
const ECAP_SIZES = {
  6.3: [[100, 5, 11], [330, 6.3, 11], [470, 8, 11.5], [1000, 10, 12.5], [2200, 10, 20]],
  10: [[47, 5, 11], [220, 6.3, 11], [470, 8, 11.5], [1000, 10, 16], [2200, 13, 20]],
  16: [[47, 5, 11], [220, 6.3, 11], [470, 8, 11.5], [1000, 10, 16], [2200, 13, 20]],
  25: [[22, 5, 11], [100, 6.3, 11], [330, 8, 11.5], [470, 10, 12.5], [1000, 10, 20], [2200, 13, 25]],
  50: [[10, 5, 11], [47, 6.3, 11], [100, 8, 11.5], [220, 10, 12.5], [470, 10, 20], [1000, 13, 20], [2200, 16, 25]],
};

export function electrolytic(props) {
  const uf = Number(props.value) || 100;
  const volts = Number(props.voltage) || 25;
  const sizes = ECAP_SIZES[volts] || ECAP_SIZES[25];
  const [, d, h] = sizes.find(([max]) => uf <= max) || sizes[sizes.length - 1];
  return { uf, farads: uf * 1e-6, volts, d, h };
}

definePart({
  type: 'capacitor-electrolytic',
  name: 'Capacitor electrolítico',
  category: 'basicos',
  description: 'Capacitor de 0.47 a 2200 µF con polaridad: la pata larga es el + y la franja del cuerpo marca la pata −.',
  keywords: 'capacitor condensador electrolitico polarizado uf microfaradios filtro fuente rc temporizador',
  insertable: true,
  props: [
    { key: 'value', label: 'Capacitancia', type: 'select', default: '100', options: ELECTROLYTIC_VALUES.map((v) => ({ value: v, label: `${v} µF` })) },
    { key: 'voltage', label: 'Voltaje nominal', type: 'select', default: '25', options: ELECTROLYTIC_VOLTS.map((v) => ({ value: v, label: `${v} V` })) },
  ],
  variants: [['10', '25'], ['100', '25'], ['1000', '16']].map(([value, voltage]) => ({ label: `Capacitor electrolítico ${value} µF ${voltage} V`, props: { value, voltage } })),
  pins: [
    { name: '-', label: '− (pata corta, lado de la franja)', kind: 'gnd', x: -PITCH / 2, y: 0, z: 0, insert: true },
    { name: '+', label: '+ (pata larga)', kind: 'power', x: PITCH / 2, y: 0, z: 0, insert: true },
  ],
  footprint: (props) => {
    const e = electrolytic(props);
    return { w: e.d + 0.5, d: e.d + 0.5, h: e.h + 1 };
  },
  help: 'Con una resistencia R en serie se carga en τ = R·C (al 63 % en τ). Conéctalo con el + hacia el voltaje más alto ' +
    'y sin pasar su voltaje nominal (deja margen): el simulador avisa si queda al revés o con más voltaje. ' +
    'Se simula su ESR (resistencia en serie); no su fuga de corriente ni su tolerancia (±20 %).',
});

// Temporizador NE555 (DIP-8) -------------------------------------------------------------------------
export const NE555_PINS = ['GND', 'TRIG', 'OUT', 'RESET', 'CTRL', 'THR', 'DIS', 'VCC'];
const NE555_LABELS = {
  GND: 'GND', TRIG: 'TRIG (disparo: bajo ⅓ VCC sube la salida)', OUT: 'OUT (salida)', RESET: 'RESET (en LOW apaga; a VCC si no se usa)',
  CTRL: 'CTRL (control: ⅔ VCC; 10 nF a GND)', THR: 'THR (umbral: sobre ⅔ VCC baja la salida)', DIS: 'DIS (descarga)', VCC: 'VCC (4.5–16 V)',
};

definePart({
  type: 'ne555',
  name: 'Temporizador NE555',
  category: 'logica',
  description: 'El temporizador clásico en DIP-8: con resistencias y un capacitor oscila (astable) o da un pulso de duración fija (monoestable).',
  keywords: '555 ne555 lm555 timer temporizador oscilador astable monoestable multivibrador',
  insertable: true,
  // Pines como en la hoja de datos: 1–4 abajo de izquierda a derecha y 5–8 arriba de derecha a izquierda.
  pins: NE555_PINS.map((name, i) => {
    const bottom = i < 4;
    const k = bottom ? i : 7 - i;
    return {
      name,
      label: `${i + 1} · ${NE555_LABELS[name]}`,
      kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
      x: (k - 1.5) * PITCH,
      y: 0,
      z: bottom ? 1.5 * PITCH : -1.5 * PITCH,
      insert: true,
    };
  }),
  footprint: { w: 10.5, d: 9, h: 6 },
  help: 'Astable: R1 de VCC a DIS (7), R2 de DIS a THR (6) y TRIG (2), que van unidos, y C de ahí a GND. ' +
    'Oscila a f = 1.44 / ((R1 + 2·R2)·C): en alto 0.693·(R1 + R2)·C y en bajo 0.693·R2·C. ' +
    'Monoestable: R de VCC a THR y DIS y C a GND; un pulso bajo en TRIG da un pulso alto de 1.1·R·C. ' +
    'Une RESET (4) a VCC y pon 10 nF de CTRL (5) a GND. Con 5 V la salida alta da ≈ 3.7 V (1.3 V menos que VCC) y la baja ≈ 0.1 V.',
});

// Placa táctil -----------------------------------------------------------------------------------------
export const PLATE = { W: 40, D: 40, LEAD: 30 };
PLATE.PIN_X = -PLATE.W / 2 - PLATE.LEAD;

definePart({
  type: 'touch-plate',
  name: 'Placa táctil (papel aluminio)',
  category: 'entradas',
  description: 'Un cuadro de papel aluminio con su cable: con la librería CapacitiveSensor y una resistencia de 1 MΩ el Arduino nota cuando lo tocas. Haz clic sostenido sobre la lámina.',
  keywords: 'capacitivo capacitive capacitivesensor sensor tactil touch aluminio lamina papel',
  props: [{ key: 'touched', label: 'Tocar la lámina', type: 'button' }],
  pins: [{ name: 'SIG', label: 'Cable de la lámina', kind: 'signal', x: PLATE.PIN_X, y: 1.0, z: 0, dir: [-1, 0, 0] }],
  footprint: { w: PLATE.W + PLATE.LEAD + 2, d: PLATE.D, h: 4, x: -PLATE.LEAD / 2 },
  help: 'La lámina, su cable y el pin tienen ≈ 20 pF respecto de tierra; al tocarla se suma la capacitancia del cuerpo (≈ 150 pF). ' +
    'Conecta el cable al pin de recepción y una resistencia de 1 MΩ entre ese pin y el de envío: ' +
    '<code>CapacitiveSensor sensor(4, 2)</code> (envío 4, recepción 2) y <code>sensor.capacitiveSensor(30)</code>. ' +
    'Solo se simula el contacto directo: no la cercanía de la mano (que se nota con 10 MΩ o más) ni una lámina cubierta.',
});
